import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Eye, EyeOff, Loader2, LogIn } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AuthShell } from "../components/AuthShell";

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const success = await login(username.trim(), password);
    if (success) navigate("/");
    setLoading(false);
  };

  return (
    <AuthShell>
      <div className="mb-8 flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
          <LogIn className="w-6 h-6 text-slate-800" />
        </div>
        <div>
          <h2 className="text-base md:text-lg font-bold text-slate-900 tracking-tight">Σύνδεση στο σύστημα</h2>
          <p className="text-sm text-slate-500 mt-1">Συνδεθείτε με το όνομα χρήστη ή το email σας.</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="username" className="text-slate-700 font-medium">Όνομα Χρήστη ή Email</Label>
          <Input id="username" type="text" placeholder="π.χ. username ή onoma@dikastirio.gr" value={username} autoComplete="username"
            onChange={(e) => setUsername(e.target.value)} required className="h-12 rounded-xl bg-slate-50/60 border-slate-200 focus-visible:ring-slate-900/20 focus-visible:border-slate-900" data-testid="username-input" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-slate-700 font-medium">Κωδικός</Label>
            <Link to="/forgot-password" className="text-xs font-medium text-slate-600 hover:text-slate-900 underline-offset-4 hover:underline" data-testid="forgot-password-link">
              Ξεχάσατε τον κωδικό;
            </Link>
          </div>
          <div className="relative">
            <Input id="password" type={showPassword ? "text" : "password"} placeholder="Εισάγετε τον κωδικό" value={password} autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)} required className="h-12 pr-11 rounded-xl bg-slate-50/60 border-slate-200 focus-visible:ring-slate-900/20 focus-visible:border-slate-900" data-testid="password-input" />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors" data-testid="toggle-password-btn">
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit-btn"
          className="w-full h-12 text-base font-semibold rounded-xl bg-slate-900 hover:bg-slate-800 shadow-[0_10px_24px_-10px_rgba(15,23,42,0.6)] hover:shadow-[0_14px_30px_-10px_rgba(15,23,42,0.7)] hover:-translate-y-0.5 transition-[transform,box-shadow,background-color] duration-200">
          {loading ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />Σύνδεση...</>) : (<><LogIn className="w-5 h-5 mr-2" />Είσοδος</>)}
        </Button>
      </form>
      <p className="mt-8 text-xs text-slate-500 text-center" data-testid="register-hint">
        Δεν έχετε λογαριασμό; <Link to="/register" className="font-semibold text-slate-800 underline-offset-4 hover:underline" data-testid="register-link">Εγγραφή στο σύστημα</Link>
        <span className="block mt-1 text-slate-400">Η εγγραφή ενεργοποιείται μετά από έγκριση του Διαχειριστή.</span>
      </p>
    </AuthShell>
  );
};

export default LoginPage;
